import { Col, Row, Tooltip } from 'antd';
import classNames from 'classnames';
import { every, find, isObject, omit, some, values } from 'lodash-es';
import { Fragment, useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useHistory } from 'react-router';

import { quoteConversions } from '../../app/data/quoteConversions';
import { shipmentConversions } from '../../app/data/shipmentConversions';
import { QuoteBadge as Badge } from '../../app/enums/QuoteBadge';
import { QuoteStage } from '../../app/enums/QuoteStage';
import { ShipmentStage } from '../../app/enums/ShipmentStage';
import routes from '../../app/routes';
import {
  EMPTY_VALUE,
  formatAccount,
  formatLocationShort,
} from '../../common/utils/formatUtils';
import { useNodeInnerText } from '../../common/utils/stringUtils';
import Whitespace from '../../components/Whitespace';
import { FAIcon } from '../../components/adapters/fontAwesomeAdapters';
import {
  AnimatedList,
  InfiniteScrollList,
} from '../../components/data/dataRenderers';
import CustomGPSIcon from '../../components/graphics/CustomGPSIcon';
import {
  Scrollbars,
  VerticalTrackTypes,
} from '../../components/layout/Scrollbars';
import { Flex1 } from '../../components/layout/layoutElements';
import { useResponsiveQueries } from '../../components/responsive/responsiveQueries';
import { BlockLink } from '../../components/typography';
import {
  CopyToClipboardButton,
  CopyToClipboardHoverArea,
} from '../../components/widgets/CopyToClipboard';
import { useAccounts } from '../../hooks/data/auth';
import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { DateTimeFormatMedium } from '../../utils/dateFormatting';
import { cssTransitionClassNames } from '../../utils/reactTransitionUtils';
import { useScrollToThis } from '../../utils/scrollUtils';
import { formatDateTimeSchedule } from '../shipmentDetail/shipmentFormats';
import MonitorNoData from './MonitorNoData';
import { QuoteBadge } from './QuoteBadge';
import { DeleteQuoteButton, DeleteShipmentButton } from './monitorButtons';
import { MonitorContext, getLocationModeInfo } from './monitorContexts';
import {
  useActivityIndicatorsStyles,
  useMonitorJobListStyles,
} from './MonitorJobList.styles';
import { useMonitorJobListWidth } from './useMonitorJobListWidth';

const MONITOR_ITEM_SCALE_TIMEOUT = 800;
const MONITOR_ITEM_SHRINK_TIMEOUT = 600;
const MONITOR_ITEM_MEDIUM_WIDTH = 530;
const MONITOR_ITEM_LARGE_WIDTH = 984;
const MONITOR_INFINITE_SCROLL_THRESHOLD = 1000;
const MonitorItemLayout = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};
const MonitorItemLayoutToClassName = {
  [MonitorItemLayout.SMALL]: 'monitor-job-list-item-sm',
  [MonitorItemLayout.MEDIUM]: 'monitor-job-list-item-md',
  [MonitorItemLayout.LARGE]: 'monitor-job-list-item-lg',
};
const MonitorItemRenderers = {
  [MonitorItemLayout.SMALL]: renderJobItemSmallLayout,
  [MonitorItemLayout.MEDIUM]: renderJobItemMediumLayout,
  [MonitorItemLayout.LARGE]: renderJobItemLargeLayout,
};

function Item({ className, jobNumber, item, children }) {
  const { styles } = useMonitorJobListStyles();
  const { push } = useHistory();
  const { activeJob, setActiveJob, locationMode } = useContext(MonitorContext);
  const { stage, quoteStatus, referenceNo, badge } = item;
  const active =
    (jobNumber && activeJob === jobNumber) ||
    (referenceNo && activeJob === referenceNo);
  const ref = useScrollToThis({ active, skipIfInScrollView: true });
  const media = useResponsiveQueries();

  const onClick = useCallback(() => {
    setActiveJob(jobNumber);
    if (stage === ShipmentStage.SAVED) {
      push(generatePath(routes.releaseShipment, { jobNumber }));
    } else if (quoteStatus === 'QUOTE') {
      if (badge !== Badge.EXPIRED) {
        push(generatePath(routes.quoting.quoteDetail, { referenceNo }));
      }
    } else {
      push(generatePath(routes.shipmentDetail, { jobNumber }));
    }
  }, [jobNumber, push, setActiveJob, stage, quoteStatus, referenceNo, badge]);

  const location = getLocationModeInfo(locationMode);

  const { customerAccounts } = useAccounts();
  const account = find(
    customerAccounts,
    acc => acc.number === item?.metadata?.qolAccount?.number
  );
  const isPartner = account?.partner;

  return (
    <BlockLink
      ref={ref}
      component="div"
      className={classNames(
        styles.monitorJobListItem,
        { active, 'monitor-job-list-item-with-top-bar': isPartner },
        className
      )}
      onClick={onClick}
      data-subject="job-list"
      data-role="item"
    >
      <div className="monitor-job-list-item-content">
        {children({ location, media })}
      </div>
      <div>
        {isPartner && (
          <div className={styles.monitorJobListTopBar}>
            <FormattedMessage id="job.partnerAccess" />:<Whitespace />
            {account.name}
          </div>
        )}
        {item.stage === ShipmentStage.SAVED && (
          <DeleteShipmentButton jobNumber={jobNumber} />
        )}
        {item.quoteStatus === 'QUOTE' && (
          <DeleteQuoteButton referenceNo={item.referenceNo} />
        )}
      </div>
    </BlockLink>
  );
}

function Column({
  className,
  labelId,
  children,
  noTitle,
  customTitle,
  allowCopy,
  copyTooltipId,
  style,
}) {
  const { styles } = useMonitorJobListStyles();
  const { ref, text } = useNodeInnerText();
  let title = text;
  if (customTitle) {
    title = customTitle;
  }
  if (noTitle) {
    title = undefined;
  }

  return (
    <CopyToClipboardHoverArea
      className={classNames(
        'monitor-job-list-column',
        styles.monitorJobListColumn,
        className
      )}
    >
      {labelId && (
        <div
          className={classNames(
            'monitor-job-list-label',
            styles.monitorJobListLabel
          )}
        >
          <FormattedMessage id={labelId} />
          {allowCopy && (
            <CopyToClipboardButton
              tooltipId={copyTooltipId}
              className="icon-11"
              iconWrapperClassName={styles.monitorJobListLabelCopyIcon}
              getContent={() => text}
            />
          )}
        </div>
      )}
      <div
        ref={ref}
        title={title}
        className={styles.monitorJobListContent}
        style={style}
      >
        {children}
      </div>
    </CopyToClipboardHoverArea>
  );
}

const DateTimeFormat = props => (
  <DateTimeFormatMedium {...props} separator=" | " />
);

function ActivityIndicatorTooltip({ tooltipId, active, children }) {
  const intl = useIntl();
  const tooltip =
    tooltipId &&
    intl.formatMessage(
      { id: tooltipId },
      {
        suffix: intl.formatMessage({
          id: active
            ? 'job.activityIndicator.suffix.active'
            : 'job.activityIndicator.suffix.inactive',
        }),
      }
    );

  return tooltip ? <Tooltip title={tooltip}>{children}</Tooltip> : children;
}

function ActivityIndicator({
  className,
  indicator,
  icon,
  tooltipId,
  children,
}) {
  const { styles } = useActivityIndicatorsStyles();
  if (!indicator) {
    return null;
  }

  return (
    <ActivityIndicatorTooltip tooltipId={tooltipId} active={indicator.active}>
      <div
        className={classNames(
          styles.activityIndicatorsIndicator,
          {
            highlight: indicator.active,
          },
          className
        )}
      >
        {icon && (
          <FAIcon icon={icon} className={styles.activityIndicatorsIcon} />
        )}
        {children}
      </div>
    </ActivityIndicatorTooltip>
  );
}

export function ActivityIndicators({
  className,
  indicatorClassName,
  indicators,
  customGPSIconSize,
  showNaLabel = true,
}) {
  const { styles } = useActivityIndicatorsStyles();
  const isAnyAvailable = some(values(indicators), isObject);
  if (!isAnyAvailable) {
    return <div>{showNaLabel && <FormattedMessage id="labels.na" />}</div>;
  }
  return (
    <div className={classNames(styles.activityIndicators, className)}>
      <ActivityIndicator
        icon="truck"
        indicator={indicators.driver}
        tooltipId="job.activityIndicator.onWay"
        className={indicatorClassName}
      />
      <ActivityIndicator
        icon="plane"
        indicator={indicators.flight}
        tooltipId="job.activityIndicator.flightTracker"
        className={indicatorClassName}
      />
      <ActivityIndicator
        indicator={indicators.device}
        tooltipId="job.activityIndicator.gpsDevice"
        className={indicatorClassName}
      >
        <CustomGPSIcon size={customGPSIconSize} />
      </ActivityIndicator>
      <ActivityIndicator
        className={classNames('orange', indicatorClassName)}
        icon="exclamation-triangle"
        indicator={indicators.serviceUpdate}
        tooltipId="job.activityIndicator.revisedEta"
      />
    </div>
  );
}

function JobBolNumber({ jobNumber, bolNumber }) {
  const isSameNumber = bolNumber && jobNumber === bolNumber;
  return (
    <Column
      labelId="job.bolNumber"
      allowCopy
      copyTooltipId="job.bolNumber.copy"
    >
      {isSameNumber ? jobNumber : `${jobNumber} / ${bolNumber}`}
    </Column>
  );
}

function QuoteNumberColumn({ jobNumber, referenceNo, navigationProperty }) {
  return (
    <Column
      labelId={`${navigationProperty}Number`}
      allowCopy
      copyTooltipId={`${navigationProperty}Number.copy`}
    >
      {jobNumber || referenceNo}
    </Column>
  );
}

function LocationColumn({ locationLabelId, item, getJobLocation }) {
  return (
    <Column labelId={locationLabelId}>
      {formatLocationShort(getJobLocation(item))}
    </Column>
  );
}

function ReadyDateColumn({ pickupDateTime }) {
  return (
    <Column labelId="job.readyDate">
      {formatDateTimeSchedule(pickupDateTime, DateTimeFormat)}
    </Column>
  );
}

function DeadlineDateColumn({ dateTime }) {
  return (
    <Column labelId="job.deadlineDate">
      {formatDateTimeSchedule(dateTime, DateTimeFormat)}
    </Column>
  );
}

function BookedOrExpiryDateColumn({ quote }) {
  const labelId =
    quote?.badge === Badge.BOOKED ? 'quote.bookedAt' : 'quote.expiration';
  const dateTime =
    quote?.badge === Badge.BOOKED
      ? quote?.bookedDateTime
      : quote?.expiryDateTime;
  return (
    <Column labelId={labelId}>
      <DateTimeFormat value={dateTime} />
    </Column>
  );
}

function AccountColumn({ account }) {
  return <Column labelId="job.accountNumber">{formatAccount(account)}</Column>;
}

function ShipperReferenceColumn({ shipperReference }) {
  const references = shipperReference.split(/\n/g);

  return (
    <Column
      labelId="shipmentDetail.shipperReference"
      customTitle={shipperReference}
    >
      {references[0] || EMPTY_VALUE}
      {references.length > 1 && '…'}
    </Column>
  );
}

function StatusColumn({ status }) {
  return <Column labelId="job.status">{status}</Column>;
}

function renderJobItemSmallLayout(
  data,
  getJobLocation,
  isShipment,
  item,
  locationLabelId,
  navigationProperty
) {
  return (
    <>
      <LocationColumn
        locationLabelId={locationLabelId}
        item={item}
        getJobLocation={getJobLocation}
      />
      {isShipment ? (
        <Column labelId="job.activityIndicators" noTitle>
          <ActivityIndicators indicators={data.indicators} />
        </Column>
      ) : (
        <Column noTitle>
          <QuoteBadge data={data} />
        </Column>
      )}
      <ReadyDateColumn pickupDateTime={data.pickupDateTime} />
      {isShipment ? (
        <DeadlineDateColumn dateTime={data.deliveryDateTime} />
      ) : (
        <BookedOrExpiryDateColumn quote={data} />
      )}

      {isShipment ? (
        <JobBolNumber jobNumber={data.jobNumber} bolNumber={data.bolNumber} />
      ) : (
        <QuoteNumberColumn
          jobNumber={data.jobNumber}
          referenceNo={data.referenceNo}
          navigationProperty={navigationProperty}
        />
      )}
      {isShipment && (
        <ShipperReferenceColumn shipperReference={data.shipperReference} />
      )}
      <StatusColumn status={data.status} />
      {isShipment && <AccountColumn account={data?.metadata?.account} />}
    </>
  );
}

function renderJobItemMediumLayout(
  data,
  getJobLocation,
  isShipment,
  item,
  locationLabelId,
  navigationProperty
) {
  return (
    <Row gutter={pxToNumber(cssVariables.spaceNorm)}>
      <Col style={{ minHeight: 32 }}>
        <LocationColumn
          locationLabelId={locationLabelId}
          item={item}
          getJobLocation={getJobLocation}
        />
        {isShipment && (
          <Column>
            <ActivityIndicators
              indicators={data.indicators}
              showNaLabel={false}
            />
          </Column>
        )}
      </Col>
      <Col>
        <ReadyDateColumn pickupDateTime={data.pickupDateTime} />
        {isShipment ? (
          <DeadlineDateColumn dateTime={data.deliveryDateTime} />
        ) : (
          <BookedOrExpiryDateColumn quote={data} />
        )}
      </Col>
      <Col>
        {isShipment ? (
          <JobBolNumber jobNumber={data.jobNumber} bolNumber={data.bolNumber} />
        ) : (
          <QuoteNumberColumn
            jobNumber={data.jobNumber}
            referenceNo={data.referenceNo}
            navigationProperty={navigationProperty}
          />
        )}
        {isShipment ? (
          <ShipperReferenceColumn shipperReference={data.shipperReference} />
        ) : (
          <StatusColumn status={data.status} />
        )}
      </Col>
      <Col>
        {isShipment ? (
          <>
            <StatusColumn status={data.status} />
            <AccountColumn account={data?.metadata?.account} />
          </>
        ) : (
          <Column noTitle>
            <QuoteBadge data={data} />
          </Column>
        )}
      </Col>
    </Row>
  );
}

function renderJobItemLargeLayout(
  data,
  getJobLocation,
  isShipment,
  item,
  locationLabelId,
  navigationProperty
) {
  return (
    <Row gutter={pxToNumber(cssVariables.spaceNorm)}>
      <Col>
        {isShipment ? (
          <>
            <JobBolNumber
              jobNumber={data.jobNumber}
              bolNumber={data.bolNumber}
            />
            <Column>
              <ActivityIndicators
                indicators={data.indicators}
                showNaLabel={false}
              />
            </Column>
          </>
        ) : (
          <QuoteNumberColumn
            jobNumber={data.jobNumber}
            referenceNo={data.referenceNo}
            navigationProperty={navigationProperty}
          />
        )}
      </Col>
      <Col>
        <LocationColumn
          locationLabelId={locationLabelId}
          item={item}
          getJobLocation={getJobLocation}
        />
      </Col>
      <Col>
        <ReadyDateColumn pickupDateTime={data.pickupDateTime} />
      </Col>
      <Col>
        {isShipment ? (
          <DeadlineDateColumn dateTime={data.deliveryDateTime} />
        ) : (
          <BookedOrExpiryDateColumn quote={data} />
        )}
      </Col>
      {isShipment && (
        <Col>
          <ShipperReferenceColumn shipperReference={data.shipperReference} />
        </Col>
      )}
      <Col>
        <StatusColumn status={data.status} />
      </Col>
      <Col>
        {isShipment ? (
          <AccountColumn account={data?.metadata?.account} />
        ) : (
          <Column noTitle>
            <QuoteBadge data={data} />
          </Column>
        )}
      </Col>
    </Row>
  );
}

function getItemLayout(containerWidth) {
  if (containerWidth < MONITOR_ITEM_MEDIUM_WIDTH) {
    return MonitorItemLayout.SMALL;
  }
  if (containerWidth < MONITOR_ITEM_LARGE_WIDTH) {
    return MonitorItemLayout.MEDIUM;
  }
  return MonitorItemLayout.LARGE;
}

export function renderJobItem(item, containerWidth) {
  const isShipment = Object.keys(item).includes('jobNumber');
  const data = isShipment
    ? shipmentConversions.graphqlToDetail(item)
    : quoteConversions.graphqlToDetail(item);
  const navigationProperty = isShipment ? 'job.job' : 'quote.quote';
  const isCardDisabled = !isShipment && data?.badge === Badge.EXPIRED;
  const itemLayout = getItemLayout(containerWidth);
  const renderItemFunction = MonitorItemRenderers[itemLayout];

  return (
    <Item
      jobNumber={data.jobNumber || data.referenceNo}
      className={classNames(MonitorItemLayoutToClassName[itemLayout], {
        CardDisabled: isCardDisabled,
      })}
      item={item}
    >
      {({ location: { labelId: locationLabelId, getJobLocation } }) => (
        <>
          {renderItemFunction(
            data,
            getJobLocation,
            isShipment,
            item,
            locationLabelId,
            navigationProperty
          )}
        </>
      )}
    </Item>
  );
}

function useMonitorJobListGroups() {
  const ACTIVE_QUOTES_GROUP = 'active';
  const GROUPS = ['saved', 'live', 'delivered'];
  const { isQuoteEnabled } = useAccounts();
  const groups = isQuoteEnabled ? [ACTIVE_QUOTES_GROUP, ...GROUPS] : GROUPS;

  return { groups, ACTIVE_QUOTES_GROUP };
}

const RENDER_EMPTY = () => <MonitorNoData />;

function GroupList({
  groupData,
  className,
  rowKey,
  isOpen,
  getTransitionProps,
  ...rest
}) {
  const { styles } = useMonitorJobListStyles();
  return (
    <InfiniteScrollList
      {...omit(groupData, ['totalRows'])}
      threshold={MONITOR_INFINITE_SCROLL_THRESHOLD}
      render={({ rows }) => (
        <AnimatedList
          dataSource={rows}
          rowKey={rowKey}
          className={classNames(styles.monitorJobList, className)}
          data-subject="monitor-job-list"
          renderEmpty={RENDER_EMPTY}
          disabled={!isOpen}
          transitionProps={row => {
            const transitionProps = getTransitionProps?.(row);
            if (transitionProps) {
              return transitionProps;
            }
            return row.stage === ShipmentStage.SAVED ||
              row.stage === QuoteStage.ACTIVE
              ? {
                  classNames: cssTransitionClassNames('monitor-item-delete', {
                    appear: false,
                    enter: false,
                    exit: true,
                  }),
                  timeout:
                    MONITOR_ITEM_SHRINK_TIMEOUT + MONITOR_ITEM_SCALE_TIMEOUT,
                }
              : { timeout: 0 };
          }}
          {...rest}
        />
      )}
    />
  );
}

function FixedHeightLayoutWrapper({ children }) {
  return (
    <Flex1>
      <Scrollbars vertical={VerticalTrackTypes.OUTSIDE}>{children}</Scrollbars>
    </Flex1>
  );
}

function SingleGroupMonitorJobList({
  data,
  className,
  rowKey,
  fixedHeightLayout,
  ...rest
}) {
  const Wrapper = fixedHeightLayout ? FixedHeightLayoutWrapper : Fragment;
  return (
    <Wrapper>
      <GroupList
        groupData={data.all}
        className={className}
        rowKey={rowKey}
        isOpen
        {...rest}
      />
    </Wrapper>
  );
}

function SingleStageMonitorJobList({
  data,
  className,
  rowKey,
  fixedHeightLayout,
  activeSection,
  ...rest
}) {
  const { ACTIVE_QUOTES_GROUP } = useMonitorJobListGroups();
  const Wrapper = fixedHeightLayout ? FixedHeightLayoutWrapper : Fragment;

  if (!data[activeSection]) {
    return null;
  }

  return (
    <Wrapper>
      <GroupList
        groupData={data[activeSection]}
        className={className}
        rowKey={activeSection === ACTIVE_QUOTES_GROUP ? 'referenceNo' : rowKey}
        isOpen
        {...rest}
      />
    </Wrapper>
  );
}

function MonitorJobList({
  className,
  rowKey = 'jobNumber',
  fixedHeightLayout,
  ...rest
}) {
  const { styles } = useMonitorJobListStyles();
  const { data, activeSection } = useContext(MonitorContext);
  const { groups } = useMonitorJobListGroups();
  const { ref, width } = useMonitorJobListWidth();

  return (
    <div ref={ref} className={styles.monitorJobListContainer}>
      {every(groups, gr => !!data[gr]) ? (
        <SingleStageMonitorJobList
          data={data}
          className={className}
          rowKey={rowKey}
          fixedHeightLayout={fixedHeightLayout}
          activeSection={activeSection}
          containerWidth={width}
          {...rest}
        />
      ) : (
        <SingleGroupMonitorJobList
          data={data}
          className={className}
          rowKey={rowKey}
          fixedHeightLayout={fixedHeightLayout}
          containerWidth={width}
          {...rest}
        />
      )}
    </div>
  );
}

export default MonitorJobList;
